import {fontStyles, theme} from '@styles/theme';
import styled, {css} from 'styled-components';
import {StyledOptionButtonProps} from '@atoms/OptionButton';

export const SelectWrapper = styled.div<StyledOptionButtonProps>`
  min-width: 0;
  width: ${({fixedWidth}) => (fixedWidth ? `${fixedWidth}px` : 'auto')};
  display: flex;
  align-items: center;
  height: 28px;
  border-radius: ${theme.roundedMedium};
  cursor: ${({inputDisabled}) => (inputDisabled ? 'default' : 'pointer')};
  position: relative;
  padding-left: 12px;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  border: ${({focus, inputDisabled, borderColor}: StyledOptionButtonProps) => {
    switch (true) {
      case inputDisabled:
        return `1px solid ${theme?.colors.basic05}`;
      case focus:
        return `1px solid ${borderColor ?? theme?.colors.basic03}`;
      default:
        return `1px solid ${borderColor ?? theme?.colors.basic05}`;
    }
  }};
  background-color: ${({backgroundColor}) => backgroundColor ?? theme.colors.basic08};
  transition: all 0.5s;
  & label {
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100% - 16px);
  }
`;

export const Label = styled.label<StyledOptionButtonProps>`
  ${fontStyles.tinyR}
  color: ${theme.colors.basic03};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: calc(100% - 21px);
  transition: 0.2s all linear;
  position: absolute;
  top: 6px;
  left: 12px;
`;

export const SelectItem = styled.div<StyledOptionButtonProps & {open?: boolean}>`
  ${fontStyles.bodyM}
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: transparent;
  border: none;
  padding: 6px 45px 6px 0;
  outline: none;
  width: calc(100% - 40px);
  white-space: ${({wrap}) => (wrap ? 'normal' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({inputDisabled, textColor}) =>
    inputDisabled ? theme.colors.basic04 : textColor};
  &:before {
    content: '';
    display: inline-block;
    position: absolute;
    right: 36px;
    width: 1px;
    height: 16px;
    background-color: ${theme.colors.basic05};
  }
  ${({showSeparator}) =>
    showSeparator === false &&
    css`
      &:before {
        display: none;
      }
    `}
  &:after {
    width: 8px;
    height: 5px;
    content: '';
    display: inline-block;
    mask-image: url(/assets/icons/caretDown.svg);
    mask-repeat: no-repeat;
    mask-size: 8px 5px;
    background-color: ${({inputDisabled, textColor}) =>
      inputDisabled ? theme.colors.basic04 : textColor};
    position: absolute;
    top: 12px;
    right: 16px;
    transition: transform 0.2s ease;
    transform: rotate(${({open}) => (open ? '180deg' : '0deg')});
  }
`;

export const SelectList = styled.div<{
  isOpen: boolean;
  position: 'top' | 'bottom';
  menuWidth: string;
  type?: 'calendar' | 'default';
  align?: 'left' | 'right';
  scrollable?: boolean;
}>`
  position: absolute;
  padding: ${({type}) => (type === 'calendar' ? 0 : '8px')};
  /* 'bottom' => open below; 'top' => open above */
  ${({position}) =>
    position === 'bottom' ? 'top: calc(100% + 8px);' : 'bottom: calc(100% + 8px);'}
  ${({align}) => (align === 'right' ? 'right: -1px;' : 'left: -1px;')}
  background-color: white;
  height: fit-content;
  max-height: ${({scrollable}) => (scrollable ? '300px' : 'none')};
  /* For custom dropdownContent (scrollable=false) size to content; for lists use menuWidth */
  width: ${({scrollable, menuWidth}) => (scrollable ? menuWidth : 'max-content')};
  outline: none;
  border-radius: ${({type}) =>
    type === 'calendar' ? theme.roundedBig : theme.roundedMedium};
  border: ${({type}) =>
    type === 'calendar' ? 'none' : `1px solid ${theme.colors.basic05}`};
  opacity: ${({isOpen}) => (isOpen ? '1' : '0')};
  display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
  z-index: 1000;
  /* fully control overflow based on scrollable */
  overflow-y: ${({scrollable}) => (scrollable ? 'auto' : 'visible')};
  overscroll-behavior: ${({scrollable}) => (scrollable ? 'contain' : 'auto')};
  scroll-behavior: auto;
  &::-webkit-scrollbar {
    width: ${({scrollable}) => (scrollable ? '6px' : '0')};
    background: #ededed;
    border-radius: 4px;
    display: ${({scrollable}) => (scrollable ? 'block' : 'none')};
  }
  &::-webkit-scrollbar-thumb {
    cursor: pointer;
    width: 4px;
    background: #b1b1b1;
    border-radius: 4px;
    &:hover {
      background-color: ${theme.colors.basic00};
    }
  }
`;

export const Option = styled.div<{
  choosed: boolean;
  disabled: boolean;
  highlighted: boolean;
  deleteOption: boolean;
}>`
  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  word-wrap: break-word;
  pointer-events: ${({disabled}) => (disabled ? 'none' : '')};
  display: ${({disabled}) => (disabled ? 'none' : '')};
  background-color: ${({highlighted}) =>
    highlighted ? `${theme.colors.accent02}` : `${theme.colors.basic08}`};
  p {
    color: ${({deleteOption, choosed}) =>
      deleteOption
        ? theme.colors.signalRed00
        : choosed
          ? theme.colors.basic00
          : theme.colors.accent01};
  }
  &:hover {
    cursor: pointer;
  }
`;

export const OptionText = styled.div`
  display: flex;
  gap: 5px;
  justify-content: left;
  align-items: center;
  padding: 8px;
`;

export const Title = styled.p`
  ${fontStyles.bodyNum}
  margin: 0;
`;

export const PlanActionsDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-radius: ${theme.roundedMedium};
  border: 1px solid ${theme.colors.basic05};
  width: fit-content;
  button {
    justify-content: flex-start;
    width: 100%;
  }
`;
